import { ExportClient } from 'api/clients/ExportClient'
import { SnapshotClient } from 'api/clients/SnapshotClient'
import { VersionClient } from 'api/clients/VersionClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Modal } from 'common/Modal/Modal'
import { Button } from 'components/Button'
import prntr from 'prntr'
import { useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { reportError } from 'utils/error'
import Styles from './PrintPreview.module.scss'

export interface PrintPreviewProps {
  articleKey: string
  onCancel: () => void
}

export const PrintPreview = ({ articleKey, onCancel }: PrintPreviewProps) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [errored, setErrored] = useState<boolean>(false)

  const cancel = () => {
    setLoading(false)
    onCancel()
  }

  const loadPrintPreview = async () => {
    try {
      setErrored(false)
      const snapShotResponse = await VersionClient.getVersionInfo(articleKey).then((res) => {
        if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Error getting version info')
        const version = res.data.article.current_version
        return SnapshotClient.getSnapshot(articleKey, version)
      })
      if (snapShotResponse.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Error getting snapshot')

      const article = snapShotResponse.data.article
      const body = {
        article: {
          title: article.title,
          content: article.elements,
          article_style: article.article_style,
          workspace_article_style: article.workspace_article_style,
        },
        page_format: 'Letter',
      }
      const exportResponse = await ExportClient.printPreview(body)
      if (exportResponse.status !== 200) throw new Error('Error getting export')
      const blob = await exportResponse.blob()
      setLoading(false)
      prntr({ printable: URL.createObjectURL(blob), type: 'pdf' })
    } catch (error) {
      setErrored(true)
      reportError(error, { severity: 'error' })
    }
  }

  useEffect(() => {
    loadPrintPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const errorModalBody = () => (
    <div className={Styles.errorModalBody}>
      <div>There was an error preparing your document to print. Please try again.</div>
      <Button size="lg" onPress={loadPrintPreview}>
        Prepare print
      </Button>
    </div>
  )

  const title = useMemo(() => {
    return errored ? <div className={Styles.errorText}>Error</div> : 'Preparing...'
  }, [errored])

  return (
    <Modal show={loading} onClose={cancel} title={title}>
      {errored ? errorModalBody() : <div>Please wait while we convert your document for printing.</div>}
    </Modal>
  )
}

export const renderPrintPreview = (props: Omit<PrintPreviewProps, 'onCancel'>) => {
  const mountPoint = document.body
  const el = document.createElement('div')
  const root = createRoot(el)
  el.id = 'print-preview-modal'
  mountPoint.appendChild(el)
  const cancel = () => root.unmount()
  root.render(<PrintPreview {...props} onCancel={cancel} />)
}
